import { useEffect, useState } from 'react';
import type { Settings } from './storage/storage.ts';

const darkQuery = () =>
  typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

/** Applies Style and Mode to <html>, following the device when Mode is System. */
export function useTheme(settings: Settings): 'light' | 'dark' {
  const [systemDark, setSystemDark] = useState(() => darkQuery()?.matches ?? false);

  useEffect(() => {
    const q = darkQuery();
    if (!q) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    q.addEventListener('change', onChange);
    return () => q.removeEventListener('change', onChange);
  }, []);

  const mode = settings.mode === 'system' ? (systemDark ? 'dark' : 'light') : settings.mode;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.style = settings.style;
    root.dataset.mode = mode;
    // The browser's own bar colour follows the page background.
    const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg || '#f6f7fb');
  }, [settings.style, mode]);

  return mode;
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
